import { XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { CreateDestinationInput, Destination, Template as TemplateQL } from '@ui/__generated__/graphql'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { NameInput } from '@ui/components/core/NameInput'
import { showToast } from '@ui/components/elements/ToastManager/ToastManager'
import { Spinner } from '@ui/components/loaders'
import { DynamicFormValues, NodeTypeEnum, Template } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { CreateDestinationQuery } from '@ui/loaders/destinations'
import { GetTemplates } from '@ui/loaders/templates'

import { ExporterForm } from '../DestinationForms/ExporterForm'

type TemplateSelectionProps = {
  onSelect: (template: TemplateQL | null) => void
}

const TemplateSelection = ({ onSelect }: TemplateSelectionProps) => {
  const { data, loading, error } = GetTemplates({ nodeType: NodeTypeEnum.EXPORT })

  const logoPathBySlug = useLogoPathBySlug(data?.getTemplates.map((template) => template.slug))

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <ApolloErrorMessage error={error} />
  }

  return (
    <>
      {data?.getTemplates.map((template) => (
        <div
          key={template.id}
          className="flex h-16 cursor-pointer items-center gap-2 rounded px-4 hover:bg-gray-50"
          onClick={() => onSelect(template)}>
          <img className="h-7 w-7" src={logoPathBySlug[template.slug || '']}></img>
          <p className="text-xs font-semibold">{template.name}</p>
        </div>
      ))}
    </>
  )
}

type CreateDestinationProps = {
  onClose: () => void
  onCreate: (destination: Destination) => void
}

export const CreateDestination = ({ onClose, onCreate }: CreateDestinationProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateQL | null>(null)

  const handleClose = () => {
    setSelectedTemplate(null)
    onClose()
  }

  const [name, setName] = useState('')

  const [createDestination, { loading: loadingCreateDestination, error: createDestinationError }] =
    CreateDestinationQuery()

  const saveDestination = async (formData: DynamicFormValues) => {
    if (!selectedTemplate || loadingCreateDestination) return

    const input: CreateDestinationInput = {
      name,
      formData,
      sliceTemplateId: selectedTemplate.id as number,
    }

    const { data } = await createDestination({ variables: { input } })

    showToast.success('Destination has been created')

    handleClose()

    onCreate(data.createDestination)
  }

  return (
    <>
      <div className="flex w-full justify-between border-b border-gray-200 p-4">
        <p className="text-base font-medium">Add Destination</p>
        <XMarkIcon className="h-6 w-6 cursor-pointer stroke-gray-500 hover:stroke-gray-700" onClick={handleClose} />
      </div>
      <div className="p-4">
        {createDestinationError && <ApolloErrorMessage error={createDestinationError} />}
        {selectedTemplate ? (
          <ExporterForm
            template={selectedTemplate as unknown as Template}
            config={{}}
            saveConfig={(values: DynamicFormValues) => {
              saveDestination(values)
            }}
            cancel={() => setSelectedTemplate(null)}
            additionalInputs={<NameInput onChange={setName} value={name} />}
          />
        ) : (
          <TemplateSelection onSelect={setSelectedTemplate} />
        )}
      </div>
    </>
  )
}
